import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../services/supabase'

const normLabel = (adv) => adv?.full_name || adv?.name || 'Unassigned'

// selectedMonth: 'YYYY-MM' string. Null = current calendar month.
export function useBoardroomData(selectedMonth = null) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(new Date())
  const [secondsUntilRefresh, setSecondsUntilRefresh] = useState(120)

  const getRefMonth = useCallback(() => {
    if (selectedMonth) return selectedMonth
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  }, [selectedMonth])

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const refMonth = getRefMonth()
      const [refYear, refMonthNum] = refMonth.split('-').map(Number)

      // Build 3 month strings: selected, prev, 2 months before
      const months = [0, 1, 2].map(offset => {
        const d = new Date(refYear, refMonthNum - 1 - offset, 1)
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      })
      const [thisMonth, prevMonth, prevPrevMonth] = months
      const fetchFrom = `${prevPrevMonth}-01`
      // Compute actual last day of thisMonth (avoids invalid dates like Apr-31)
      const lastDay = new Date(refYear, refMonthNum, 0).getDate()
      const fetchTo = `${thisMonth}-${String(lastDay).padStart(2, '0')}`

      const [txRes, polRes] = await Promise.all([
        supabase
          .from('policy_transactions')
          .select('policy_invoice_no, transaction_date, advisor_share_excl, product, client')
          .eq('active', true)
          .gte('transaction_date', fetchFrom)
          .lte('transaction_date', fetchTo),
        supabase
          .from('policies')
          .select('policy_number, policy_advisors(advisor_id, advisors(id, name, full_name))'),
      ])

      const allTx = txRes.data || []

      const policyAdvisorMap = {}
      for (const pol of polRes.data || []) {
        if (!pol.policy_number) continue
        const key = String(pol.policy_number).trim().toUpperCase()
        policyAdvisorMap[key] = (pol.policy_advisors || []).map(pa => pa.advisors).filter(Boolean)
      }

      // Slice by transaction_date month
      const thisTx     = allTx.filter(tx => tx.transaction_date?.startsWith(thisMonth))
      const prevTx     = allTx.filter(tx => tx.transaction_date?.startsWith(prevMonth))
      const prevPrevTx = allTx.filter(tx => tx.transaction_date?.startsWith(prevPrevMonth))

      const sumAdv = (txList) => txList.reduce((s, tx) => s + (Number(tx.advisor_share_excl) || 0), 0)
      const pctChange = (cur, prev) => prev > 0 ? ((cur - prev) / prev) * 100 : 0
      const distinctCount = (txList, field) => new Set(txList.map(tx => tx[field]).filter(Boolean)).size

      const totalCommission = sumAdv(thisTx)
      const previousTotalCommission = sumAdv(prevTx)
      const prevPrevTotal = sumAdv(prevPrevTx)
      const growthVsMonth = pctChange(totalCommission, previousTotalCommission)
      const previousGrowth = pctChange(previousTotalCommission, prevPrevTotal)

      const policiesWritten = distinctCount(thisTx, 'policy_invoice_no')
      const previousPolicies = distinctCount(prevTx, 'policy_invoice_no')
      const clientsServed = distinctCount(thisTx, 'client')
      const previousClientsServed = distinctCount(prevTx, 'client')

      const avgPerPolicy = policiesWritten > 0 ? totalCommission / policiesWritten : 0
      const prevAvgPerPolicy = previousPolicies > 0 ? previousTotalCommission / previousPolicies : 0

      const productMap = {}
      thisTx.forEach(tx => {
        const prod = (tx.product || 'Other').trim()
        productMap[prod] = (productMap[prod] || 0) + (Number(tx.advisor_share_excl) || 0)
      })
      const commissionByProduct = Object.entries(productMap)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 6)
        .map(([name, amount]) => ({ name, amount }))

      const buildAdvMap = (txList) => {
        const map = {}
        for (const tx of txList) {
          const key = String(tx.policy_invoice_no || '').trim().toUpperCase()
          const advisors = key ? (policyAdvisorMap[key] || []) : []
          const targets = advisors.length > 0
            ? advisors
            : [{ id: 'unassigned', full_name: 'Unassigned' }]
          for (const adv of targets) {
            if (!map[adv.id]) map[adv.id] = { name: normLabel(adv), commission: 0, policies: new Set() }
            map[adv.id].commission += Number(tx.advisor_share_excl) || 0
            if (tx.policy_invoice_no) map[adv.id].policies.add(tx.policy_invoice_no)
          }
        }
        return map
      }

      const thisAdvMap = buildAdvMap(thisTx)
      const prevAdvMap = buildAdvMap(prevTx)

      const topBrokers = Object.entries(thisAdvMap)
        .filter(([id]) => id !== 'unassigned')
        .sort(([, a], [, b]) => b.commission - a.commission)
        .slice(0, 5)
        .map(([id, b], idx) => ({
          rank: idx + 1,
          name: b.name,
          commission: b.commission,
          policies: b.policies.size,
          prevCommission: prevAdvMap[id]?.commission || 0,
        }))

      const topBroker = topBrokers[0]
        ? { name: topBrokers[0].name, commission: topBrokers[0].commission, previousCommission: topBrokers[0].prevCommission }
        : { name: '—', commission: 0, previousCommission: 0 }

      const activeBrokers = Object.keys(thisAdvMap).filter(id => id !== 'unassigned').length
      const previousActiveBrokers = Object.keys(prevAdvMap).filter(id => id !== 'unassigned').length

      setData({
        totalCommission, previousTotalCommission, prevPrevTotal,
        topBroker, growthVsMonth, previousGrowth,
        policiesWritten, previousPolicies,
        clientsServed, previousClientsServed,
        avgPerPolicy, previousAvgPerPolicy: prevAvgPerPolicy,
        commissionByInsurer: commissionByProduct,
        commissionByProduct,
        topBrokers, activeBrokers, previousActiveBrokers,
        monthlyTotals: [prevPrevTotal, previousTotalCommission, totalCommission],
        selectedMonth: thisMonth,
      })
      setLastUpdated(new Date())
      setSecondsUntilRefresh(120)
      setError(null)
    } catch (err) {
      console.error('useBoardroomData error:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [getRefMonth])

  useEffect(() => {
    fetchData()
    // Only auto-refresh when viewing current month
    if (!selectedMonth) {
      const dataInterval = setInterval(fetchData, 120000)
      const countdownInterval = setInterval(() => {
        setSecondsUntilRefresh(prev => (prev > 0 ? prev - 1 : 120))
      }, 1000)
      return () => {
        clearInterval(dataInterval)
        clearInterval(countdownInterval)
      }
    }
  }, [fetchData, selectedMonth])

  return { data, loading, error, lastUpdated, secondsUntilRefresh, refetch: fetchData }
}
